<template>
  <Loader :value="promises.fetchOne" :message="$metaInfo.title" class="openhouse-mutate">
    <div class="container view">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumbs">
        <template #actions>
          <Actions placement="openhouse" :query="{ openhouse: id }">
            <template #bottom>
              <ButtonComponent
                v-if="!isNew && !isDisabled"
                :label="$t('delete')"
                @click="removeDialog = true"
                :icon="['fal', 'times']"
              />
            </template>
          </Actions>
          <ButtonComponent
            :label="isNew ? $t('create') : $t('save')"
            theme="primary"
            type="submit"
            :disabled="!validator.passes"
            :icon="['fal', isNew ? 'plus' : 'arrow-to-bottom']"
            :promise="promises.mutate"
            :class="{ 'elder-button--disabled': Boolean(isDisabled) }"
            v-tooltip.right="isDisabled"
            @click="submit"
          />
        </template>
      </HeaderComponent>
      <Card tag="form" @submit.prevent="submit" class="openhouse-mutate__form" v-tooltip="isDisabled">
        <template #header>
          <FontAwesomeIcon :icon="['fal', 'sign']" size="6x" />
        </template>
        <template #default>
          <div class="openhouse-mutate__inputs">
            <InputComponent
              data-field="fromDate"
              :label="labels.date"
              required
              :is-valid="validator.fields.from.valid"
              :validation-message="validator.fields.from.error"
              :disabled="Boolean(isDisabled)"
            >
              <InputDate v-model="fromDate" :min="minDate" />
            </InputComponent>

            <template v-if="fromDate">
              <InputComponent
                v-model="fromTime"
                type="time"
                :label="labels.from"
                required
                :is-valid="validator.fields.from.valid"
                :disabled="Boolean(isDisabled)"
              />

              <InputComponent
                v-model="toTime"
                type="time"
                :label="labels.to"
                required
                :is-valid="validator.fields.to.valid"
                :validation-message="validator.fields.to.error"
                :disabled="Boolean(isDisabled)"
              />
            </template>

            <InputComponent
              :value="data.message"
              data-field="message"
              :label="labels.message"
              :is-valid="validator.fields.message.valid"
              :validation-message="validator.fields.message.error"
              :disabled="Boolean(isDisabled)"
            >
              <textarea v-model="data.message" class="elder-input__element"></textarea>
            </InputComponent>

            <InputComponent :label="$t('openhouseReferences') + '?'" data-field="references">
              <template #default>
                <ChecklistComponent
                  v-model="data.references"
                  :items="fetchResidentials"
                  :compare="(a, b) => a.ref === b.ref"
                  :disabled="Boolean(isDisabled)"
                  class="elder-input__element"
                >
                  <template #default>
                    <CheckboxComponent
                      :value="!data.references.length"
                      :label="$tc('theWholeProject', 1)"
                      :disabled="Boolean(isDisabled)"
                      @input="() => (data.references = [])"
                    />
                    <hr />
                  </template>
                </ChecklistComponent>
              </template>
            </InputComponent>
          </div>
        </template>
      </Card>
      <RemoveComponent
        :show.sync="removeDialog"
        :title="title"
        :action="() => remove({ id })"
        @onSuccess="
          $router.push({
            name: 'project-dashboard',
            params: { id: $path('project.id', item) },
          })
        "
        :resource="$tc('openhouse')"
      />
    </div>
  </Loader>
</template>

<script>
import Validator from '@/mixins/validator'
import { DateTransformer, TimeTransformer } from '@/utils/transformers'
import { mapState, mapActions, mapMutations } from 'vuex'
import { mapi18n } from '@/i18n'
import ResidentialApi from '@/api/Residential'
import Model from '@/models/Openhouse'
import moment from 'moment'

import HeaderComponent from '@/components/Header'
import ChecklistComponent from '@/components/Select/Checklist'
import Card from '@/components/Card'
import RemoveComponent from '@/components/Remove'
import { InputComponent } from 'vue-elder-input'
import { CheckboxComponent } from 'vue-elder-checkbox'
import InputDate from '@/components/InputDate'
import Actions from '@/components/Actions'

export default {
  mixins: [Validator],
  props: {
    id: String,
    project: String,
  },
  data() {
    return {
      data: Model(),
      removeDialog: false,
      validator: {
        rules: {
          from: 'required|date',
          to: 'required|date',
          message: 'present',
        },
        labels: 'labels',
        data: 'data',
      },
    }
  },
  watch: {
    id: {
      handler(id = '') {
        return this.fetch([
          { id, project: this.project, isNew: this.isNew },
          {
            query: 'ProjectOpenhouse',
            transform: v => ({ ...(v.Openhouse || {}), project: v.Reference }),
          },
        ])
      },
      immediate: true,
    },
    item: {
      handler(value) {
        this.data = Model(value)
      },
      immediate: true,
    },
  },
  computed: {
    minDate() {
      return moment().format('YYYY-MM-DD')
    },
    fromDate: DateTransformer('data.from'),
    fromTime: TimeTransformer('data.from'),
    toTime: TimeTransformer('data.to', { date: 'data.from' }),
    ...mapState('Openhouse', ['item', 'promises']),
    ...mapi18n('labels', ['from', 'to', 'message', 'date']),
    ...mapi18n('i18n', {
      loadingOpenhouse() {
        return this.$t('loading', { resource: this.$tc('openhouse', 1) }) + '...'
      },
    }),
    isNew() {
      return !this.id
    },
    title() {
      if (!this.item.from) return this.i18n.loadingOpenhouse
      return `${moment(this.item.from).format('lll')} - ${moment(this.item.to).format('LT')}`
    },
    isDisabled() {
      return (
        this.$path('item.project.metadata.disabledFields.project.openhouses') ||
        this.$path('item.project.metadata.disabledFields.unit.openhouses')
      )
    },
    breadcrumbs() {
      return [
        { title: this.$tc('project', 2), action: { name: 'projects' } },
        {
          title: this.$path('item.project.name'),
          action: {
            name: 'project-dashboard',
            params: { id: this.$path('item.project.id') },
          },
        },
        { title: this.$tc('openhouse', 2) },
        { title: this.$metaInfo.title },
      ]
    },
  },
  methods: {
    ...mapActions('Openhouse', ['fetch', 'mutate', 'remove']),
    submit() {
      if (this.isDisabled) return

      let data = { ...this.data }
      if (this.isNew) data.project = this.project
      this.mutate([
        data,
        {
          strategy(a, b) {
            return {
              ...a,
              ...b,
              project: a.project,
            }
          },
        },
      ]).then(data => {
        if (!this.isNew) return
        if (this.$route.query.returnTo) return this.$router.push(this.$route.query.returnTo)
        this.$router.replace({
          name: 'project-openhouse-edit',
          params: { id: data.id, project: this.project },
        })
      })
    },
    fetchResidentials() {
      return ResidentialApi.get(
        { filter: { project: this.project }, sort: { key: 'number', value: 1 } },
        { query: 'ResidentialSearch' },
      ).then(data =>
        data.map(e => ({
          value: { ref: e.id, onModel: 'Residential' },
          label: `${this.$tc('residential')} ${e.number}`,
        })),
      )
    },
  },
  metaInfo() {
    return {
      title: this.isNew ? this.$t('createOpenhouse') : this.title,
    }
  },
  components: {
    InputComponent,
    HeaderComponent,
    ChecklistComponent,
    CheckboxComponent,
    RemoveComponent,
    Card,
    InputDate,
    Actions,
  },
}
</script>

<style lang="scss">
.openhouse-mutate {
  background-color: $background-grey;

  &__form {
    margin: 0 auto;
    max-width: 550px;

    [type='submit'] {
      margin-right: auto;
    }
  }

  &__inputs {
    display: grid;
    align-items: center;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: $form-gap-row / 2 $form-gap-column;
    align-items: flex-start;
  }

  [data-field='message'],
  [data-field='fromDate'],
  [data-field='references'] {
    grid-column-end: span 2;
  }

  [data-field='references'] {
    .elder-input__element {
      padding: 0;
    }
    .select-checklist__items {
      padding: 1.1em;
      max-height: 250px;
      overflow-y: auto;

      @include scrollShadow();
    }
  }
}
</style>
